import { fetchNotificationGroups } from "@rpforms/shared/build/actions/notification_groups";
import { Content } from "@rpforms/shared/build/components/layout/Content";
import { EmptySpace } from "@rpforms/shared/build/components/universal/EmptySpace";
import { Loader } from "@rpforms/shared/build/components/universal/Loader";
import PageNavbar from "@rpforms/shared/build/components/universal/PageNavbar";
import { range } from "@rpforms/shared/build/utils";
import { push } from "connected-react-router";
import React, { useEffect, useState, useMemo } from "react";
import { Pagination } from "react-bootstrap";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useSearchParam } from "react-use";
import { useParams, useLocation } from "react-router-dom";
import styled from "styled-components";
import { ListRow } from "../../components/forms/FormListRow";
import { NotificationRow } from "../../components/notifications/NotificationRow";
import GroupNotificationLinks from "../../components/notifications/GroupNotificationLinks";

const Centered = styled.div`
    display: flex;
    justify-content: center;
`;

const generateDescription = (groupName: string) =>
    `Hier finden Sie alle Benachrichtigungen zum Thema ${groupName}.`;

const VISIBLE_NOTIFICATION_GROUPS = new Set(["Akku Wartung", "Anstehende Wartungen"]);

const ListNotificationPage = () => {
    const notificationGroups = useSelector<any, any>(
        (state) => state.notificationGroups,
        shallowEqual
    );
    const initialPage = parseInt(useSearchParam("page"), 10) || 1;
    const [page, setPage] = useState(initialPage);
    const dispatch = useDispatch();
    const pagesOffset = 5;
    const { slug } = useParams<{ slug?: string }>();
    const isGroupFiltered = !!slug && slug !== "all";

    useEffect(() => {
        dispatch(fetchNotificationGroups(page));
    }, [dispatch, page]);

    useEffect(() => {
        const base = isGroupFiltered ? `/notifications/group/${slug}` : "/notifications/all";
        dispatch(push(`${base}?page=${page}`));
    }, [dispatch, isGroupFiltered, slug, page]);

    const filteredGroups = useMemo(() => {
        const visibleGroups = (notificationGroups.notification_groups || []).filter(
            (group) => group && VISIBLE_NOTIFICATION_GROUPS.has(group.name)
        );

        if (!isGroupFiltered) return visibleGroups;
        return visibleGroups.filter((g) => g.slug === slug || g.unique_name === slug);
    }, [notificationGroups.notification_groups, isGroupFiltered, slug]);

    if (
        notificationGroups.isLoading &&
        (notificationGroups.notification_groups?.length ?? 0) === 0
    ) {
        return <Loader />;
    }

    const totalPages = notificationGroups.pages || 1;
    const pageNumbers = () => {
        const start = Math.max(2, page - pagesOffset);
        const end = Math.min(totalPages - 1, page + pagesOffset);
        return [1, ...range(start, end), totalPages];
    };

    const currentGroup = isGroupFiltered && filteredGroups.length > 0 ? filteredGroups[0] : null;
    const pageTitle = currentGroup ? currentGroup.name : "Benachrichtigungen";
    const pageDescription = currentGroup
        ? generateDescription(currentGroup.name)
        : "Hier können Sie Ihre Benachrichtigungen einsehen.";

    return (
        <>
            <PageNavbar title={pageTitle} description={pageDescription} />
            <Content>
                <GroupNotificationLinks />

                {filteredGroups.length === 0 ? (
                    <EmptySpace
                        title={"Keine Einträge gefunden."}
                        description={
                            "Übertrage ein ausgefülltes Formular dieser Kategorie mit einem iPad damit es hier erscheint."
                        }
                        cta={false}
                        link=""
                    />
                ) : (
                    <>
                        {filteredGroups.map((group) => (
                            <div style={{ marginTop: "20px" }} key={group.id}>
                                <h3>{group.name}</h3>
                                {group.notifications.map((n) => (
                                    <ListRow key={n.id}>
                                        <NotificationRow notification={n} />
                                    </ListRow>
                                ))}
                            </div>
                        ))}
                    </>
                )}
            </Content>
            {false && (
                <Centered>
                    <Pagination>
                        {pageNumbers().map((p) => (
                            <Pagination.Item
                                active={p === page}
                                key={`page_${p}`}
                                onClick={() => setPage(p)}
                            >
                                {p}
                            </Pagination.Item>
                        ))}
                    </Pagination>
                </Centered>
            )}
        </>
    );
};

export default ListNotificationPage;
